import React from 'react';

import { FEATURES } from 'appConstants';
import { useFeatureFlags } from 'hooks';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../../i18n/TModules';
import { LIMIT_PERIODS, LimitPeriod, WizardLimits } from '../wizardModel';
import { Checkbox, RadioGroup } from './controls';
import { DateField, SelectField, TextField } from './fields';
import { Hint, Line, Sub, Words } from './layout';

const TL = `${TOrbit}.wizard.limits`;

interface ILimitLineProps {
  on: boolean;
  onToggle: (on: boolean) => void;
  leadIn: string;
  value: string;
  onChange: (value: string) => void;
  unit?: string;
  hint?: string;
  period?: { value: LimitPeriod; onChange: (value: LimitPeriod) => void };
}

/** 6778:20788: a checkbox, its lead-in, a 120 number, then a unit, a hint or a period. */
export const LimitLine: React.FC<ILimitLineProps> = ({
  on,
  onToggle,
  leadIn,
  value,
  onChange,
  unit,
  hint,
  period
}) => {
  const { t } = useMultiLanguageTranslation();
  return (
    <Line>
      <Checkbox checked={on} onChange={onToggle} label={leadIn} />
      <Words $muted={!on}>{leadIn}</Words>
      <TextField
        label={leadIn}
        placeholder="0"
        inputMode="numeric"
        value={value}
        disabled={!on}
        onChange={onChange}
      />
      {unit !== undefined ? <Words $muted>{unit}</Words> : null}
      {period !== undefined ? (
        <SelectField<LimitPeriod>
          label={unit ?? leadIn}
          value={period.value}
          disabled={!on}
          options={LIMIT_PERIODS.map(option => ({
            value: option,
            label: t(`${TL}.period.${option}`)
          }))}
          onChange={period.onChange}
        />
      ) : null}
      {hint !== undefined ? <Hint>{hint}</Hint> : null}
    </Line>
  );
};

interface ILimitsProps {
  limits: WizardLimits;
  onChange: (patch: Partial<WizardLimits>) => void;
}

/**
 * 6778:20825-:20844: auto or self enrolment; the enrol-by date shows with the module's own
 * `enrollable_until` flag and only applies to self enrolment, as in the module.
 */
export const EnrolmentFields: React.FC<
  ILimitsProps & { withTime: boolean; children?: React.ReactNode }
> = ({ limits, onChange, withTime, children }) => {
  const { t } = useMultiLanguageTranslation();
  const flags = useFeatureFlags();
  const enrolBy = flags[FEATURES.ENROLLABLE_UNTIL.key] === true;
  return (
    <>
      <Line>
        <Words>{t(`${TL}.enrolment`)}</Words>
        <RadioGroup<'auto' | 'self'>
          name="orbit-wizard-enrolment"
          label={t(`${TL}.enrolment`)}
          value={limits.autoEnrol ? 'auto' : 'self'}
          options={[
            { value: 'auto', label: t(`${TL}.autoEnrol`) },
            { value: 'self', label: t(`${TL}.selfEnrol`) }
          ]}
          onChange={choice => onChange({ autoEnrol: choice === 'auto' })}
        />
      </Line>
      <Sub>
        {enrolBy ? (
          <Line>
            <Checkbox
              checked={limits.enrolByOn}
              disabled={limits.autoEnrol}
              label={t(`${TL}.enrolBy`)}
              onChange={enrolByOn => onChange({ enrolByOn })}
            />
            <Words $muted>{t(`${TL}.enrolBy`)}</Words>
            <DateField
              label={t(`${TL}.enrolBy`)}
              value={limits.enrolBy}
              withTime={withTime}
              disabled={limits.autoEnrol || !limits.enrolByOn}
              onChange={value => onChange({ enrolBy: value })}
            />
          </Line>
        ) : null}
        {children}
      </Sub>
    </>
  );
};

/** Quest, Progress and Games: the campaign's start and its optional end (6778:22854). */
export const ScheduleFields: React.FC<ILimitsProps> = ({ limits, onChange }) => {
  const { t } = useMultiLanguageTranslation();
  return (
    <>
      <Line>
        <Words>{t(`${TL}.start`)}</Words>
        <DateField
          label={t(`${TL}.start`)}
          value={limits.start}
          withTime
          onChange={start => onChange({ start })}
        />
      </Line>
      <Line>
        <Checkbox
          checked={limits.endOn}
          label={t(`${TL}.end`)}
          onChange={endOn => onChange({ endOn })}
        />
        <Words $muted={!limits.endOn}>{t(`${TL}.end`)}</Words>
        <DateField
          label={t(`${TL}.end`)}
          value={limits.end}
          withTime
          disabled={!limits.endOn}
          onChange={end => onChange({ end })}
        />
      </Line>
    </>
  );
};

/** "Users must complete this … within [n] days", under self enrolment (6778:22854). */
export const WindowLine: React.FC<ILimitsProps & { leadIn: string }> = ({
  limits,
  onChange,
  leadIn
}) => {
  const { t } = useMultiLanguageTranslation();
  return (
    <LimitLine
      on={limits.windowOn}
      onToggle={windowOn => onChange({ windowOn })}
      leadIn={leadIn}
      value={limits.windowDays}
      onChange={windowDays => onChange({ windowDays })}
      unit={t(`${TL}.windowUnit`)}
    />
  );
};
